import type {
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
} from '@tanstack/react-table';
import { useTable } from '@tanstack/react-table';
import { useMemo } from 'react';
import { StateCleanup } from './components/StateCleanup';
import type { BaseDataProviderProps } from './DataProvider';
import { Context, type DataProviderContext } from './DataProviderContext';
import { createTableFeatures } from './features';
import { DefaultColumnFilter } from './filter/DefaultColumnFilter';
import { FilterProvider } from './filter/FilterContext';
import { useTableState } from './hooks/useTableState';
import type { ResolvedColumnDef } from './internal/getTableColumnDef';
import type { TableOptions, TableState } from './tanstackTypes';
import { useExtendedColumnDefs } from './useExtendedColumnDefs';

export interface DataProviderServerSideProps<
  TData extends RowData,
  TTexts = never,
> extends BaseDataProviderProps<TData, TTexts> {
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (filter: ColumnFiltersState) => void;
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  pagination: PaginationState;
  onPaginationChange: (paging: PaginationState) => void;
  rowCount: number;
}

export const DataProviderServerSide = <TData extends RowData, TTexts = never>({
  children,
  columnDefs,
  initialData,
  initialState,
  actions = [],
  isLoading = false,
  isRowClickable,
  isRowHighlighted,
  onRowClick,
  toolbarControls,
  id,

  onColumnFiltersChange,
  columnFilters,
  enableSorting = false,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  rowCount,
}: DataProviderServerSideProps<TData, TTexts>) => {
  const extendedColumnDefs = useExtendedColumnDefs<TData, TTexts>(columnDefs);
  const { tableState, updateTableState } = useTableState(id);
  const state: Partial<TableState> = {
    ...tableState,
  };
  const defaultColumn: Partial<ResolvedColumnDef<TData, TTexts>> = {
    Filter: DefaultColumnFilter,
  };
  const features = useMemo(
    () =>
      createTableFeatures({
        enableFilters: true,
        enablePagination: true,
        enableSorting,
        clientSideFiltering: false,
        clientSidePagination: false,
        clientSideSorting: false,
      }),
    [enableSorting]
  );

  const options: TableOptions<TData> = {
    features,
    columns: extendedColumnDefs,
    data: initialData,
    state,
    initialState: initialState,
    enableSorting: false,
    enableFilters: false,
    enableGrouping: false,
    enableRowSelection: false,
    manualFiltering: true,
    manualGrouping: true,
    manualExpanding: true,
    manualPagination: true,
    manualSorting: true,
    defaultColumn,
    onColumnOrderChange: (updater) => updateTableState('columnOrder', updater),
    onColumnVisibilityChange: (updater) =>
      updateTableState('columnVisibility', updater),
  };

  if (enableSorting) {
    if (!sorting || !onSortingChange) {
      throw new Error('sorting and onSortingChange are required');
    }
    options.enableSorting = true;
    options.onSortingChange = (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    };
    state.sorting = sorting;
  }

  // Column filter
  options.enableFilters = true;
  options.onColumnFiltersChange = (updater) => {
    const next =
      typeof updater === 'function' ? updater(columnFilters) : updater;
    onColumnFiltersChange(next);
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };
  state.columnFilters = columnFilters;

  // Pagination
  options.onPaginationChange = (updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater;
    onPaginationChange(next);
  };
  options.rowCount = rowCount;
  state.pagination = pagination;

  const table = useTable(options);

  return (
    <Context.Provider
      value={
        {
          table,
          actions,
          toolbarControls,
          isLoading,
          isRowClickable,
          isRowHighlighted,
          onRowClick,
          getRowCount: () => rowCount,
          isPaginationEnabled: true,
        } as DataProviderContext<TData>
      }
    >
      <FilterProvider>
        <StateCleanup />
        {children}
      </FilterProvider>
    </Context.Provider>
  );
};
